import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Button,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import {
  defaultLoad,
  dimensionalUnits,
  loadSchema,
  weightUnits,
  weightUnitsOptions,
} from 'utils/localization/types';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import { LocaleContext } from 'providers/locale-settings-context';
import Loads from './Loads';

const LoadDetailsForm = ({
  loadOnly,
  loads,
  fullTruckload,
  totalShipmentWeight,
  shipmentWeightUnit,
  addLoad,
  onRemoveLoad,
  onLoadFieldChange,
  onTotalShipmentWeightChange,
}) => {
  const { t } = useTranslation();
  const [selectedLoads, setSelectedLoads] = useState([]);
  const { localeData } = useContext(LocaleContext);
  useEffect(() => {
    setSelectedLoads(loads);
  }, [loads]);

  const addLoadhandler = () => {
    let prevLoads = [...selectedLoads];
    prevLoads.push({
      ...defaultLoad,
      weight_units:
        selectedLoads[0]?.weight_units ||
        localeData?.weight_units ||
        weightUnits[0],
      dimensional_units:
        selectedLoads[0]?.dimensional_units ||
        localeData?.dimensional_units ||
        dimensionalUnits[0],
    });
    addLoad(prevLoads);
  };

  const removeLoad = (index) => {
    return [
      ...selectedLoads.slice(0, index),
      ...selectedLoads.slice(index + 1),
    ];
  };

  const handleRemoveLoad = (index) => {
    const newLoads = removeLoad(index);
    onRemoveLoad(newLoads, index);
  };

  const handleLoadFieldChange = (field, value, index) => {
    const currentLoads = selectedLoads;
    let parsedValue;
    if (
      field === 'stackable' ||
      field === 'type' ||
      field === 'weight_units' ||
      field === 'dimensional_units'
    ) {
      parsedValue = value;
    } else if (field === 'weight' || field === 'height') {
      parsedValue = parseFloat(value);
      parsedValue = !Number.isNaN(parsedValue) ? parsedValue : undefined;
    } else {
      parsedValue = parseInt(value, 10);
      parsedValue = !Number.isNaN(parsedValue) ? parsedValue : undefined;
    }

    if (field === 'weight_units' || field === 'dimensional_units') {
      const loads = (currentLoads || []).map((load) => {
        return {
          ...load,
          [field]: parsedValue,
        };
      });
      return onLoadFieldChange(loads, field, value, index);
    }

    const load = {
      ...selectedLoads[index],
      [field]: parsedValue,
    };

    const loads = [
      ...currentLoads.slice(0, index),
      load,
      ...currentLoads.slice(index + 1),
    ];

    onLoadFieldChange(loads, field, value, index);
  };

  function renderShipmentWeight() {
    if (!fullTruckload) {
      return (
        <DescriptionListItem
          label={t('TOTAL_SHIPMENT_WEIGHT')}
          className="text-sm font-medium text-gray-500"
        >
          <dd className="mt-1 text-sm text-gray-900">
            {totalShipmentWeight} {shipmentWeightUnit}
          </dd>
        </DescriptionListItem>
      );
    }
    return (
      <div className="flex items-end gap-3">
        <CargomaticInput
          type="number"
          label={t('TOTAL_SHIPMENT_WEIGHT')}
          placeholder={t('TOTAL_SHIPMENT_WEIGHT')}
          min={0}
          value={totalShipmentWeight}
          onChange={(value) => onTotalShipmentWeightChange(value)}
        />
        <CargomaticSelect
          showCaption
          label={t('WEIGHT_UNIT')}
          selected={shipmentWeightUnit}
          className="w-24"
          items={weightUnitsOptions}
          onSelect={(item) => {
            handleLoadFieldChange('weight_units', item.value, 0);
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        {!fullTruckload && (
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold my-0">{t('LOAD')}</h3>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addLoadhandler}
              aria-label={t('ADD')}
            >
              <Plus className="size-4" />
              {t('ADD')}
            </Button>
          </div>
        )}
        {!loadOnly && renderShipmentWeight()}
      </header>
      {!fullTruckload && (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[140px]">{t('TYPE')}</TableHead>
                <TableHead className="min-w-[110px]">{t('QUANTITY')}</TableHead>
                <TableHead className="min-w-[110px]">{t('LENGTH')}</TableHead>
                <TableHead className="min-w-[110px]">{t('WIDTH')}</TableHead>
                <TableHead className="min-w-[110px]">{t('HEIGHT')}</TableHead>
                <TableHead className="min-w-[150px]">
                  {t('DIMENSIONAL_UNIT')}
                </TableHead>
                {!loadOnly && (
                  <TableHead className="min-w-[120px]">
                    {t('TOTAL_WEIGHT')}
                  </TableHead>
                )}
                {!loadOnly && (
                  <TableHead className="min-w-[130px]">
                    {t('WEIGHT_UNIT')}
                  </TableHead>
                )}
                <TableHead className="min-w-[110px] text-center">
                  {t('STACKABLE_TITLE')}?
                </TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedLoads.map((load, i) => {
                return (
                  <Loads
                    loadOnly={loadOnly}
                    index={i}
                    key={i}
                    load={load}
                    onLoadFieldChange={(field, value) =>
                      handleLoadFieldChange(field, value, i)
                    }
                    onRemoveLoad={() => handleRemoveLoad(i)}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

LoadDetailsForm.propTypes = {
  fullTruckload: PropTypes.bool.isRequired,
  loads: PropTypes.arrayOf(
    PropTypes.shape({
      ...loadSchema,
    }),
  ),
  onLoadFieldChange: PropTypes.func.isRequired,
  addLoad: PropTypes.func.isRequired,
  onRemoveLoad: PropTypes.func.isRequired,
  onTotalShipmentWeightChange: PropTypes.func.isRequired,
};

LoadDetailsForm.defaultProps = {
  loads: [],
};

export default LoadDetailsForm;
